import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Card from "components/card";
import Button from "components/button";
import { getEvents, rsvpEvent, cancelRsvpEvent } from "services/events";
import { useToast } from "components/Toast";

interface EventItem {
  id: number;
  title?: string;
  name?: string;
  description?: string;
  event_date?: string;
  event_time?: string;
  location?: string;
  capacity?: number;
  going_count?: number;
  maybe_count?: number;
  creator?: { firstname?: string; lastname?: string; name?: string };
  rsvpStatus?: string | null;
}

const UserEvents: React.FC = () => {
  const [events, setEvents] = useState<EventItem[]>([]);
  const [loading, setLoading] = useState(true);
  const toast = useToast();

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      setLoading(true);
      try {
        const res = await getEvents();
        const data = res.data?.data ?? res.data ?? [];
        if (mounted) setEvents(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error(err);
        if (mounted) setEvents([]);
        toast.showError?.("Failed to load events");
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    return () => {
      mounted = false;
    };
  }, [toast]);

  return (
    <div className="p-4">
      <div className="mb-4 flex items-center justify-between">
        <h1 className="text-2xl font-bold">Events</h1>
        <Link to="/user/events/new">
          <Button size="sm">Create</Button>
        </Link>
      </div>

      <Card extra="p-4">
        {loading ? (
          <div>Loading...</div>
        ) : events.length === 0 ? (
          <div className="text-sm text-gray-500">No upcoming events</div>
        ) : (
          <div className="space-y-3">
            {events.map((e) => (
              <article
                key={e.id}
                className="rounded-lg bg-white p-3 shadow-sm dark:bg-navy-700"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-sm font-semibold">
                      {e.name || e.title}
                    </h3>
                    <div className="text-xs text-gray-500">
                      {e.event_date} {e.event_time ? `• ${e.event_time}` : ""}
                      {e.location ? (
                        <span className="ml-2">• {e.location}</span>
                      ) : null}
                    </div>
                    <div className="text-xs text-gray-400">
                      {typeof e.going_count !== "undefined"
                        ? `${e.going_count} going`
                        : ""}
                      {typeof e.maybe_count !== "undefined"
                        ? ` • ${e.maybe_count} maybe`
                        : ""}
                      {typeof e.capacity !== "undefined"
                        ? ` • capacity ${e.capacity}`
                        : ""}
                    </div>
                    <p className="mt-2 line-clamp-2 text-sm text-gray-600 dark:text-gray-300">
                      {e.description}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Link to={`/user/events/${e.id}`}>
                      <Button size="sm" variant="outline">
                        View Details
                      </Button>
                    </Link>
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
};

export default UserEvents;
